import { AsyncPipe, JsonPipe, NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  inject,
} from '@angular/core';
import {
  CodecraftQuizInstance,
  CodecraftQuizInstanceApiResult,
} from '@codecraft-works/data-models';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { map, Observable } from 'rxjs';
import { QuizService } from '../data-access/quiz.service';
import { QuizTableTitleLinkCellComponent } from './quiz-table-title-link-cell.component';

@Component({
  standalone: true,
  selector: 'codecraft-quiz-table-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 class="col-12 mt-3 pb-2">My Quizzes</h2>
    <ag-grid-angular
      *ngIf="rowData$ | async as rowData"
      #agGrid
      class="ag-theme-material mt-2"
      [animateRows]="true"
      [paginationPageSize]="10"
      [rowData]="rowData"
      [pinnedBottomRowData]="avgScore"
      [columnDefs]="colDefs"
      [getRowClass]="getRowClass"
      domLayout="autoHeight"
      [enableCellChangeFlash]="true"
      [components]="components"
    >
    </ag-grid-angular>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
      :host ::ng-deep .average-row {
        font-weight: bold;
        border-top: 2px solid #ccc;
      }
    `,
  ],
  imports: [AgGridModule, AsyncPipe, NgIf],
})
export class CodecraftQuizTableViewComponent implements OnInit, AfterViewInit {
  @Input() quizData$: Observable<CodecraftQuizInstance[]>;
  rowData$: Observable<any[]>;
  avgScore: any[] = [];
  private quizService = inject(QuizService);

  @ViewChild('agGrid') agGrid: AgGridAngular;
  @ViewChild('agGrid', { read: ElementRef }) agGridElementRef: ElementRef;

  gridWidth: number;

  components = {
    quizTableTitleLinkCellComponent: QuizTableTitleLinkCellComponent,
  };

  constructor(private cdr: ChangeDetectorRef) {}

  // Helper method for safe timestamp conversion
  private convertToDate(value: any): Date {
    if (value instanceof Date) {
      return value;
    }

    if (value && typeof value.toDate === 'function') {
      return value.toDate();
    }

    // If object with _seconds and _nanoseconds (serialized Firestore Timestamp)
    if (
      value &&
      typeof value === 'object' &&
      typeof value._seconds === 'number' &&
      typeof value._nanoseconds === 'number'
    ) {
      return new Date(
        value._seconds * 1000 + Math.floor(value._nanoseconds / 1e6)
      );
    }

    return new Date();
  }

  fullTimeDateFormatter = (params) => {
    // If this is the average row, return empty string for date fields
    if (params.data && params.data.isAverageRow) {
      return '';
    }

    const value = this.convertToDate(params.value);

    if (value instanceof Date) {
      return (
        value.toLocaleDateString() +
        ' ' +
        value.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    } else {
      return '';
    }
  };

  quizProjectFields = {
    name: {
      field: 'name',
      headerName: 'Quiz Name',
      resizable: true,
      sortable: true,
      filter: true,
      minWidth: 230,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      cellRenderer: 'quizTableTitleLinkCellComponent',
    },
    created: {
      field: 'created',
      headerName: 'Date Taken',
      resizable: true,
      sortable: true,
      filter: 'agDateColumnFilter',
      minWidth: 200,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      valueFormatter: this.fullTimeDateFormatter,
    },
    score: {
      field: 'score',
      headerName: 'Your Score',
      resizable: true,
      sortable: true,
      filter: true,
      minWidth: 100,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      valueFormatter: (params) => {
        return `${params.value}%`;
      },
    },
    passingScore: {
      field: 'passingScore',
      headerName: 'Passing Score',
      resizable: true,
      sortable: true,
      filter: true,
      minWidth: 120,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      valueFormatter: (params) => {
        if (params.data && params.data.isAverageRow && params.value === null) {
          return '';
        }
        return `${params.value}%`;
      },
    },
    attemptCount: {
      field: 'attemptCount',
      headerName: 'Attempts',
      resizable: true,
      sortable: true,
      filter: true,
      minWidth: 120,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      valueFormatter: (params) => {
        if (params.data && params.data.isAverageRow && params.value === null) {
          return '';
        }
        const rowData = params.data;
        return `${params.value} / ${rowData.maxAttempts}`;
      },
    },
  };

  colDefs = [
    this.quizProjectFields.name,
    this.quizProjectFields.created,
    this.quizProjectFields.score,
    this.quizProjectFields.passingScore,
    this.quizProjectFields.attemptCount,
  ];

  quizProjectColumnDefs = {
    mobile: [this.quizProjectFields.name, this.quizProjectFields.created],
    medium: [
      this.quizProjectFields.name,
      this.quizProjectFields.created,
      this.quizProjectFields.score,
      this.quizProjectFields.passingScore,
    ],
    large: [
      this.quizProjectFields.name,
      this.quizProjectFields.created,
      this.quizProjectFields.score,
      this.quizProjectFields.passingScore,
      this.quizProjectFields.attemptCount,
    ],
    xl: [
      this.quizProjectFields.name,
      this.quizProjectFields.created,
      this.quizProjectFields.score,
      this.quizProjectFields.passingScore,
      this.quizProjectFields.attemptCount,
    ],
  };

  ngOnInit(): void {
    this.rowData$ = this.quizData$.pipe(
      map((data) => {
        const processedData = data.map((quiz) => {
          const newQuiz: any = { ...quiz };

          newQuiz.quizInstance = quiz as CodecraftQuizInstance;

          newQuiz.modified = this.convertToDate(quiz.modified);
          newQuiz.created = this.convertToDate(quiz.created);

          const totalQuestions =
            quiz.questions instanceof Map
              ? quiz.questions.size
              : Object.keys(quiz.questions || {}).length;

          newQuiz.score =
            totalQuestions > 0
              ? Math.round((quiz.correctCount / totalQuestions) * 100)
              : 0;

          return newQuiz;
        });

        // Calculate average score for pinned row
        const validScores = processedData.filter((quiz) => quiz.score > 0);
        const averageScore =
          validScores.length > 0
            ? Math.round(
                validScores.reduce((sum, quiz) => sum + quiz.score, 0) /
                  validScores.length
              )
            : 0;

        this.avgScore = [
          {
            name: 'Average Score',
            created: null,
            score: averageScore,
            passingScore: null,
            attemptCount: null,
            isAverageRow: true,
          },
        ];

        return processedData;
      }),
      map((data) => {
        this.cdr.detectChanges();
        return data;
      })
    );
  }

  ngAfterViewInit(): void {
    this.setColumnDefs(this.quizProjectColumnDefs);
  }

  getRowClass = (params) => {
    if (params.data && params.data.isAverageRow) {
      return 'average-row';
    }
    return '';
  };

  setColumnDefs(columns) {
    if (this.agGrid) {
      this.agGrid.api.setColumnDefs([]);
      if (window.innerWidth < 767.98) {
        this.agGrid.api.setColumnDefs(columns.mobile);
      } else if (window.innerWidth > 767.99 && window.innerWidth < 992) {
        this.agGrid.api.setColumnDefs(columns.medium);
      } else if (window.innerWidth > 992 && window.innerWidth < 1281) {
        this.agGrid.api.setColumnDefs(columns.large);
      } else if (window.innerWidth > 1281) {
        this.agGrid.api.setColumnDefs(columns.xl);
      }
    } else {
      if (window.innerWidth < 767.98) {
        this.colDefs = columns.mobile;
      } else if (window.innerWidth > 767.99 && window.innerWidth < 992) {
        this.colDefs = columns.medium;
      } else if (window.innerWidth > 992 && window.innerWidth < 1281) {
        this.colDefs = columns.large;
      } else if (window.innerWidth > 1281) {
        this.colDefs = columns.xl;
      }
    }
  }
}
